import { Component } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {
  IdentityWebApiClient, ListUsersRequest, IUser, ICompany, User
} from 'identity-webapi-client';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, BreadcrumbService, StateService, TaggingService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { ResourceType, Tag } from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss']
})
export class UserListComponent extends SituBaseComponent {
  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100]
  } as UsersListViewControls;
  
  userDataSource = new MatTableDataSource<IUser>();
  selectedRole: string | undefined = undefined;
  roleNames: string[] = [];
  emailFilter = "";
  enabledOnly = false;
  companies: ICompany[] = [];
  userIdParam: string | null = null;
  user: IUser | null = null;
  resourceTypeEnum = ResourceType.User;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  get tags(): Tag[] {
    return this.taggingService.tags.filter(t => t.isDeleted == false
      && t.resourceType === this.resourceTypeEnum);
  }

  private _selectAll: boolean = false;
  public get selectAll(): boolean {
    return this._selectAll;
  }
  public set selectAll(value: boolean) {
    this._selectAll = value;
    this.userDataSource.data.forEach(user => {
      (user as any).checked = value;
    });
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private identityWebApiClient: IdentityWebApiClient,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private userService: UserService,
    private taggingService: TaggingService) {
      super(authService, stateService);
      this.userIdParam = this.route.snapshot.paramMap.get('userId');
    }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadUsers();
    this.loadRoles();
  }

  protected onSelectedCompanyChange(): void {
    this.loadUsers();
    this.loadRoles();
  }

  onClickUser(user: IUser) {
    this.user = user;
    this.router.navigate(['admin/user', user.id]);
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadUsers();
  }
  
  newUser() {
    this.router.navigate(['/admin/create-user']);
  }

  loadUsers() {
    this.loading = true;

    let request = new ListUsersRequest()
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.companyId = this.selectedCompany?.id;
    request.emailFilter = this.emailFilter;
    request.enabledOnly = this.enabledOnly;
    request.role = this.selectedRole;
    request.includeTags = true;

    return this.identityWebApiClient.listUsers(request).then(response => {
      this.userDataSource.data = response.items ?? [];
      this.viewControls.totalItems = response.totalItems ?? 0;
      this.user = this.userDataSource.filteredData.find(u => u.id === this.userIdParam) ?? null;
      this.loading = false;
    }, error => {
      this.loading = false;
    });
  }

  onClickViewAsUser(id: string, email: string) {
    if (!id) {
      return;
    }

    this.userService.viewAsUser(id);
  }

  displayRoles(user: User): string {
    return user?.roles?.map(x => x.name).join(", ") ?? "";
  }

  showViewAs(user: User) {
    let isAdmin = user.roles?.some(x => x.name == Role.SituSystemsAdmin);
    let isCurrentUser = user.id == this.authService.getUserId();
    return user.isEnabled && !isAdmin && !isCurrentUser;
  }

  onCheckBoxChanged() {
    this._selectAll = false;
  }

  private loadRoles() {
    this.roleNames = Object.keys(Role).map(key => (Role as any)[key]).sort();
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

interface UsersListViewControls {
  pageSize: number;
  pageIndex: number;
  pageNumber: number;
  totalItems: number;
  pageSizeOptions: number[];
}
